/**
 * Where this customer is, in four lines, before anybody scrolls.
 *
 * The page it replaces opened with an unlabeled definition list holding
 * "Status: active" and "Mode: pandp" — the organization's database fields,
 * which answer a question nobody was asking. What somebody wants on arrival is
 * how far along this is and when it has to be done.
 */
import { formatDayUS } from '../../../shared/dates';
import page from '../../../shared/Page.module.css';
import own from './Onboarding.module.css';
import { StageStatusBadge } from './StageStatusBadge';
import type { Onboarding } from './types';

const day = formatDayUS;

export function OnboardingHeader({ data }: { data: Onboarding }) {
  const target = day(data.targetGoLiveDate);
  const percent = data.requiredStages === 0
    ? 0
    : Math.round((data.completeStages / data.requiredStages) * 100);

  return (
    <div>
      <h1 className={page.title}>{data.orgName}</h1>
      <p className={page.subtitle}>
        Organization onboarding — taking a new customer from signed to running.
      </p>

      <div className={own.headerFacts}>
        <span>
          <StageStatusBadge status={data.ready ? 'complete' : 'inProgress'} />
        </span>
        {/*
          * "REQUIRED", because the navigation shows nine and this says seven.
          * Readiness and Go live hold no requirements of their own — they
          * summarize the other seven — so a bare "2 of 7" beside a list of nine
          * reads as a contradiction and invites somebody to go looking for the
          * two that are missing.
          */}
        <span>
          {data.completeStages} of {data.requiredStages} required stages complete
        </span>
        {target && <span>Target go-live: {target}</span>}
        {!target && <span>No target date given</span>}
      </div>

      <div
        className={own.progressTrack}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={data.requiredStages}
        aria-valuenow={data.completeStages}
        aria-label="Stages complete"
      >
        <div
          className={`${own.progressFill} ${data.ready ? own.progressFillDone : ''}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
